/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ContactSelectorModal.tsx - Selector de Contactos Estilo iOS Premium
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @version 1.0.0
 * @description Modal selector integrado con useContactsDatabase
 * @architecture Componente reutilizable para Scheduler
 * 
 * FEATURES:
 * - ✅ Integración con useContactsDatabase hook
 * - ✅ Búsqueda en tiempo real
 * - ✅ Filtros por grupo y tag
 * - ✅ Selección múltiple con checkboxes iOS-style
 * - ✅ Contador de seleccionados
 * - ✅ Preview rápido
 * - ✅ Animaciones suaves Apple-style
 * - ✅ Colores Aqua #2ca799
 * - ✅ Dark/Light mode compatible
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useMemo } from 'react';
import { Modal, Form, Badge, ListGroup, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faSearch,
  faCheck,
  faCheckCircle,
  faTimes,
  faFilter,
  faTags,
  faUserGroup
} from '@fortawesome/free-solid-svg-icons';

// Types
import type { ContactDB, ContactGroup, ContactTag } from '../../../types/scheduler-shared.types';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface ContactSelectorModalProps {
  show: boolean;
  onHide: () => void;
  contacts: ContactDB[];
  selectedContactIds: Set<string>;
  onContactToggle: (id: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  groups?: ContactGroup[];
  tags?: ContactTag[];
  onConfirm?: (selectedIds: Set<string>) => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export const ContactSelectorModal: React.FC<ContactSelectorModalProps> = ({
  show,
  onHide,
  contacts,
  selectedContactIds,
  onContactToggle,
  onSelectAll,
  onClearAll,
  groups = [],
  tags = [],
  onConfirm
}) => {
  
  // ───────────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────────
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGroup, setFilterGroup] = useState<string>('all');
  const [filterTag, setFilterTag] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // ───────────────────────────────────────────────────────────────────────
  // FILTERED CONTACTS
  // ───────────────────────────────────────────────────────────────────────
  
  const filteredContacts = useMemo(() => {
    // Filtrar solo contactos activos (no bounced ni unsubscribed)
    let filtered = contacts.filter(c => c.status === 'active' || c.status === 'inactive');
    
    // Search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(term) ||
        c.phone.includes(term) ||
        c.email?.toLowerCase().includes(term) ||
        c.company?.toLowerCase().includes(term)
      );
    }
    
    // Filter by group
    if (filterGroup !== 'all') {
      filtered = filtered.filter(c => c.groups.includes(filterGroup));
    }
    
    // Filter by tag
    if (filterTag !== 'all') {
      filtered = filtered.filter(c => c.tags.includes(filterTag));
    }
    
    return filtered;
  }, [contacts, searchTerm, filterGroup, filterTag]);
  
  const selectedCount = selectedContactIds.size;
  const allFilteredSelected = filteredContacts.length > 0 && 
    filteredContacts.every(c => selectedContactIds.has(c.id));
  
  // ───────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────────
  
  const handleToggleFiltered = () => {
    if (allFilteredSelected) {
      // Deseleccionar todos los filtrados
      filteredContacts.forEach(c => {
        if (selectedContactIds.has(c.id)) {
          onContactToggle(c.id);
        }
      });
    } else {
      // Seleccionar todos los filtrados
      filteredContacts.forEach(c => {
        if (!selectedContactIds.has(c.id)) {
          onContactToggle(c.id);
        }
      });
    }
  };
  
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm(selectedContactIds);
    }
    onHide();
  };
  
  const handleClose = () => {
    setSearchTerm('');
    setFilterGroup('all');
    setFilterTag('all');
    setShowFilters(false);
    onHide();
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER
  // ───────────────────────────────────────────────────────────────────────
  
  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      style={{ zIndex: 100001 }}
    >
      <Modal.Header 
        closeButton
        style={{
          backgroundColor: 'var(--bs-body-bg)',
          borderBottom: '1px solid var(--bs-border-color)',
          padding: '20px 24px'
        }}
      >
        <Modal.Title style={{
          fontSize: '1.25rem',
          fontWeight: 600,
          color: 'var(--bs-body-color)'
        }}>
          <FontAwesomeIcon icon={faUsers} style={{ color: '#2ca799', marginRight: '12px' }} />
          Seleccionar Destinatarios
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{
        padding: '0',
        backgroundColor: 'var(--bs-body-bg)',
        maxHeight: '70vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Search & Stats Bar */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid var(--bs-border-color)',
          backgroundColor: 'var(--bs-body-bg)',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <InputGroup className="mb-3">
            <InputGroup.Text style={{
              backgroundColor: 'var(--bs-body-bg)',
              border: '1px solid var(--bs-border-color)',
              borderRight: 'none',
              borderRadius: '8px 0 0 8px'
            }}>
              <FontAwesomeIcon icon={faSearch} style={{ color: '#2ca799' }} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Buscar por nombre, teléfono, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                border: '1px solid var(--bs-border-color)',
                borderLeft: 'none',
                borderRight: 'none',
                boxShadow: 'none'
              }}
            />
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                padding: '0 16px',
                border: '1px solid var(--bs-border-color)',
                borderLeft: 'none',
                borderRadius: '0 8px 8px 0',
                background: showFilters ? 'rgba(44, 167, 153, 0.1)' : 'var(--bs-body-bg)',
                color: showFilters ? '#2ca799' : 'var(--bs-body-color)',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <FontAwesomeIcon icon={faFilter} />
            </button>
          </InputGroup>
          
          {/* Filters */}
          {showFilters && (
            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '12px',
              padding: '12px',
              backgroundColor: 'rgba(44, 167, 153, 0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(44, 167, 153, 0.2)'
            }}>
              {groups.length > 0 && (
                <Form.Group style={{ flex: 1 }}>
                  <Form.Label style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--bs-secondary)',
                    marginBottom: '6px'
                  }}>
                    <FontAwesomeIcon icon={faUserGroup} className="me-1" />
                    Grupo
                  </Form.Label>
                  <Form.Select
                    value={filterGroup}
                    onChange={(e) => setFilterGroup(e.target.value)}
                    size="sm"
                    style={{
                      borderRadius: '6px',
                      border: '1px solid var(--bs-border-color)'
                    }}
                  >
                    <option value="all">Todos los grupos</option>
                    {groups.map(g => (
                      <option key={g.id} value={g.id}>
                        {g.name}{g.count ? ` (${g.count})` : ''}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}
              
              {tags.length > 0 && (
                <Form.Group style={{ flex: 1 }}>
                  <Form.Label style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--bs-secondary)',
                    marginBottom: '6px'
                  }}>
                    <FontAwesomeIcon icon={faTags} className="me-1" />
                    Tag
                  </Form.Label>
                  <Form.Select
                    value={filterTag}
                    onChange={(e) => setFilterTag(e.target.value)}
                    size="sm"
                    style={{
                      borderRadius: '6px',
                      border: '1px solid var(--bs-border-color)'
                    }}
                  >
                    <option value="all">Todos los tags</option>
                    {tags.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name}{t.count ? ` (${t.count})` : ''}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}
            </div>
          )}
          
          {/* Stats */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '12px'
          }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--bs-secondary)' }}>
              {filteredContacts.length} contactos disponibles
            </div>
            
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Badge
                bg="primary"
                style={{
                  backgroundColor: '#2ca799',
                  fontSize: '0.813rem',
                  padding: '6px 12px',
                  borderRadius: '6px'
                }}
              >
                {selectedCount} seleccionados
              </Badge>
              
              {filteredContacts.length > 0 && (
                <button
                  onClick={handleToggleFiltered}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid var(--bs-border-color)',
                    borderRadius: '6px',
                    background: 'var(--bs-body-bg)',
                    color: '#2ca799',
                    fontSize: '0.813rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontWeight: 500
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(44, 167, 153, 0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--bs-body-bg)'}
                >
                  {allFilteredSelected ? 'Deseleccionar' : 'Seleccionar'} visibles
                </button>
              )}
              
              {selectedCount > 0 && (
                <button
                  onClick={onClearAll}
                  style={{
                    padding: '6px 12px',
                    border: 'none',
                    borderRadius: '6px',
                    background: 'rgba(242, 84, 91, 0.1)',
                    color: '#F2545B',
                    fontSize: '0.813rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontWeight: 500
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(242, 84, 91, 0.2)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(242, 84, 91, 0.1)'}
                >
                  Limpiar todo
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Contacts List */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px'
        }}>
          {filteredContacts.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 24px',
              color: 'var(--bs-secondary)'
            }}>
              <FontAwesomeIcon
                icon={faUsers}
                style={{ fontSize: '3rem', opacity: 0.3, marginBottom: '16px' }}
              />
              <div style={{ fontSize: '0.875rem' }}>
                {searchTerm || filterGroup !== 'all' || filterTag !== 'all'
                  ? 'No se encontraron contactos con los filtros aplicados'
                  : 'No hay contactos disponibles'}
              </div>
            </div>
          ) : (
            <ListGroup variant="flush">
              {filteredContacts.map((contact) => {
                const isSelected = selectedContactIds.has(contact.id);
                
                return (
                  <ListGroup.Item
                    key={contact.id}
                    onClick={() => onContactToggle(contact.id)}
                    style={{
                      cursor: 'pointer',
                      border: 'none',
                      borderBottom: '1px solid var(--bs-border-color)',
                      padding: '12px 16px',
                      backgroundColor: isSelected
                        ? 'rgba(44, 167, 153, 0.08)'
                        : 'transparent',
                      transition: 'all 0.2s ease',
                      borderRadius: '8px',
                      marginBottom: '4px'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = 'rgba(44, 167, 153, 0.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {/* Checkbox iOS style */}
                      <div style={{
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        border: `2px solid ${isSelected ? '#2ca799' : 'var(--bs-border-color)'}`,
                        backgroundColor: isSelected ? '#2ca799' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        transition: 'all 0.2s ease'
                      }}>
                        {isSelected && (
                          <FontAwesomeIcon
                            icon={faCheck}
                            style={{ color: '#fff', fontSize: '0.7rem' }}
                          />
                        )}
                      </div>
                      
                      {/* Contact info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          color: 'var(--bs-body-color)',
                          marginBottom: '2px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {contact.name}
                        </div>
                        
                        <div style={{
                          fontSize: '0.75rem',
                          color: 'var(--bs-secondary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {contact.phone}
                          {contact.email && ` • ${contact.email}`}
                        </div>
                        
                        {/* Tags */}
                        {contact.tags.length > 0 && (
                          <div style={{
                            marginTop: '6px',
                            display: 'flex',
                            gap: '4px',
                            flexWrap: 'wrap'
                          }}>
                            {contact.tags.slice(0, 3).map(tag => (
                              <Badge
                                key={tag}
                                bg="light"
                                text="dark"
                                style={{
                                  fontSize: '0.688rem',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  backgroundColor: 'rgba(44, 167, 153, 0.15)',
                                  color: '#2ca799',
                                  fontWeight: 500
                                }}
                              >
                                {tag}
                              </Badge>
                            ))}
                            {contact.tags.length > 3 && (
                              <Badge
                                bg="light"
                                text="muted"
                                style={{
                                  fontSize: '0.688rem',
                                  padding: '2px 8px',
                                  borderRadius: '4px'
                                }}
                              >
                                +{contact.tags.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          )}
        </div>
      </Modal.Body>
      
      <Modal.Footer style={{
        backgroundColor: 'var(--bs-body-bg)',
        borderTop: '1px solid var(--bs-border-color)',
        padding: '16px 24px'
      }}>
        <button
          onClick={handleClose}
          style={{
            padding: '10px 20px',
            border: '1px solid var(--bs-border-color)',
            borderRadius: '8px',
            background: 'var(--bs-body-bg)',
            color: 'var(--bs-body-color)',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          Cancelar
        </button>
        
        <button
          onClick={handleConfirm}
          disabled={selectedCount === 0}
          style={{
            padding: '10px 24px',
            border: 'none',
            borderRadius: '8px',
            background: selectedCount === 0 ? 'var(--bs-secondary)' : '#2ca799',
            color: '#fff',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: selectedCount === 0 ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            opacity: selectedCount === 0 ? 0.5 : 1
          }}
          onMouseEnter={(e) => {
            if (selectedCount > 0) {
              e.currentTarget.style.background = '#259989';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedCount > 0) {
              e.currentTarget.style.background = '#2ca799';
            }
          }}
        >
          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
          Confirmar ({selectedCount})
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ContactSelectorModal;